import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  ButtonWizard,
  LinkWizard,
  LinkWizardSo,
  TextInput,
  TurnstileCaptcha,
  TurnstileCaptchaRef,
} from '../../components';
import { UserIcon } from '@heroicons/react/24/outline';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { EMAIL_REGEX } from '../../constants';
import {
  useForgotPasswordMutation,
  useGetAuthConfigurationQuery,
} from '../../store';
import { useNotification } from '../../contexts';
import { extractErrorMessageFromAPI } from '../../utils';
import globeImage from '../../assets/images/globe1.png';
import { Link } from 'react-router-dom';
import PublicAuthLayout from './PublicAuthLayout';
import softgelLogoImg from '../../assets/images/softgel_logo.png';
import softgelBgImg from '../../assets/images/softgel_bg.png';
import denLogo from '../../assets/images/den_logo_image.svg';
import hathLogo from '../../assets/images/hathway_logo.png';

export interface ISigninForm {
  email: string;
}

export function ForgetPassword() {
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const { data: authConfiguration } = useGetAuthConfigurationQuery();
  const captchaEnabled = Boolean(authConfiguration?.data?.captchaEnabled);
  const captchaRef = useRef<TurnstileCaptchaRef>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const { showNotification } = useNotification();
  const { control, formState, handleSubmit } = useForm<ISigninForm>({
    defaultValues: {
      email: '',
    },
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const clientCode = import.meta.env.VITE_ENV_CLIENT_CODE;
  const isSoftgel = clientCode === 'SOFTGEL';
  const logoSrc = isSoftgel
    ? softgelLogoImg
    : clientCode === 'HATHWAY'
      ? hathLogo
      : denLogo;
  const primaryBtnClass = isSoftgel
    ? 'bg-[#9d0b2d] hover:bg-[#7f0823]'
    : 'bg-blue-600 hover:bg-blue-700';

  async function handleSubmitForm(data: ISigninForm) {
    if (captchaEnabled && !captchaToken) {
      showNotification('error', 'Error', 'Please Complete Security Verification');
      return;
    }
    const parsedData = {
      ...data,
      email: data.email?.trim()?.toLowerCase(),
      ...(captchaToken ? { captchaToken } : {}),
    };
    try {
      await forgotPassword(parsedData).unwrap();
      showNotification(
        'success',
        'Success',
        'Password reset link sent successfully',
      );
      setIsSubmitted(true);
    } catch (err) {
      captchaRef.current?.reset();
      setCaptchaToken(null);
      showNotification('error', 'Error', extractErrorMessageFromAPI(err));
    }
  }

  const formContentInner = (
    <div className="w-full max-w-md space-y-8">
        {/* Header */}
        {!isSubmitted ? (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
              Forgot password?
            </h2>
            <p className="text-sm text-gray-600">
              Enter your email to receive reset instructions
            </p>
          </div>
        ) : (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
              Check your email
            </h2>
            <p className="text-sm text-gray-600">
              Password reset link has been sent
            </p>
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {!isSubmitted && (
            <form className="space-y-5">
              <TextInput
                name={'email'}
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: EMAIL_REGEX,
                    message: 'Please enter a valid email address',
                  },
                }}
                label={'Email Address'}
                labelClassName="text-sm font-medium text-gray-700"
                formState={formState}
                config={{
                  prefix: <UserIcon className="w-4 h-4 text-gray-400" />,
                  placeholder: 'you@company.com',
                  className: 'h-11 text-sm',
                }}
              />

              {captchaEnabled && (
                <TurnstileCaptcha
                  ref={captchaRef}
                  action="forgot_password"
                  enabled={captchaEnabled}
                  onVerify={setCaptchaToken}
                  onExpire={() => setCaptchaToken(null)}
                  onError={() => setCaptchaToken(null)}
                  className="my-1"
                />
              )}

              <ButtonWizard
                onBtnClick={handleSubmit(handleSubmitForm)}
                addOnClass={`w-full h-11 ${primaryBtnClass} text-white rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md`}
                loading={isLoading}
              >
                Send Reset Link
              </ButtonWizard>
            </form>
          )}

          <div
            className={
              isSubmitted ? '' : 'mt-5 pt-5 border-t border-gray-200'
            }
          >
            {isSoftgel ? (
              <LinkWizardSo
                href="/signin"
                addOnClass="flex items-center justify-center gap-2 text-sm font-medium"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to login
              </LinkWizardSo>
            ) : (
              <LinkWizard
                href="/signin"
                addOnClass="flex items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to login
              </LinkWizard>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()}{' '}
          <Link
            target="_blank"
            to="https://sapphiretech.co.in"
            className="text-gray-600 hover:text-blue-600 transition-colors font-medium"
            rel="noopener noreferrer"
          >
            Sapphire Technocrats Pvt Ltd.
          </Link>{' '}
          &mdash; All rights reserved
        </p>
      </div>
  );

  if (isSoftgel) {
    return (
      <div className="h-screen overflow-hidden">
        <PublicAuthLayout
          logoSrc={logoSrc}
          leftBgImageSrc={softgelBgImg}
          accentTextClass="text-teal-500"
        >
          {formContentInner}
        </PublicAuthLayout>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden flex font-sans bg-gray-50">
      {/* Left Side - Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-2/5 relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        {/* Globe Image */}
        <img
          src={globeImage}
          alt="Globe"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-auto opacity-20 pointer-events-none"
          loading="eager"
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-8 text-white h-full">
          <div className="space-y-6 max-w-lg">
            {/* Logo */}
            <div className="flex items-center">
              <img src={logoSrc} alt="Client logo" className="w-20 h-auto" loading="eager" />
            </div>

            {/* Title */}
            <div className="space-y-3">
              <h1 className="text-5xl font-bold leading-[1.1] tracking-tight">
                Enterprise Portal
              </h1>
              <div className="h-1 w-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
              <p className="text-lg text-blue-100 leading-relaxed">
                Password recovery
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Reset Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        {formContentInner}
      </div>
    </div>
  );
}

// import { useForm } from 'react-hook-form';
// import {
//   ButtonWizard,
//   HeadingWizard,
//   LinkWizard,
//   TextInput,
// } from '../../components';
// import { UserIcon } from '@heroicons/react/24/outline';
// import { ArrowLeftIcon } from '@heroicons/react/24/solid';
// import { EMAIL_REGEX } from '../../constants';
// import denLogo from '../../assets/images/den_logo.png';
// import signInImage from '../../assets/images/signIn.svg';
// import { useForgotPasswordMutation } from '../../store';
// import { useNotification } from '../../contexts';
// import { useState } from 'react';
// import { extractErrorMessageFromAPI } from '../../utils';

// export interface ISigninForm {
//   email: string;
// }
// export function ForgetPassword() {
//   const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
//   const { showNotification } = useNotification();
//   const { control, formState, handleSubmit } = useForm<ISigninForm>({
//     defaultValues: {
//       email: '',
//     },
//   });
//   const [isSubmitted, setIsSubmitted] = useState(false);

//   async function handleSubmitForm(data: ISigninForm) {
//     const parsedData = {
//       ...data,
//       email: data.email?.trim()?.toLowerCase(),
//     };
//     try {
//       await forgotPassword(parsedData).unwrap();
//       showNotification(
//         'success',
//         'Success',
//         'Password reset link sent successfully',
//       );
//       setIsSubmitted(true);
//     } catch (err) {
//       console.log(err);
//       showNotification('error', 'Error', extractErrorMessageFromAPI(err));
//       console.error(err);
//     }
//   }
//   return (
//     <div className="h-screen grid sm:grid-cols-2 grid-cols-1">
//       <div className="overflow-hidden flex flex-col items-center justify-center bg-slate-200">
//         <div
//           className="w-96 rounded-lg py-12 px-12 flex flex-col gap-8 bg-white"
//           style={{
//             boxShadow: 'rgba(0, 0, 0, 0.15) 0px 2px 8px',
//           }}
//         >
//           <div className="flex items-center gap-2 justify-center">
//             <img src={denLogo} alt="logo" className="w-18" />
//           </div>
//           <div className="">
//             <HeadingWizard addOnClass="text-slate-800 mb-1">
//               {isSubmitted ? 'Check your email' : 'Forgot Password?'}
//             </HeadingWizard>

//             <p className="text-slate-800 text-xs py-1 mb-8">
//               {isSubmitted
//                 ? `We've sent you a link to reset your password. Please check your inbox.`
//                 : `No worries, we'll send you reset instructions.`}
//             </p>
//             {!isSubmitted && (
//               <form className="flex flex-col gap-2.5">
//                 <TextInput
//                   name={'email'}
//                   control={control}
//                   rules={{
//                     required: '* This field is required',
//                   }}
//                   label={'Email'}
//                   formState={formState}
//                   addOnClass={'text-secondary-100'}
//                   config={{
//                     prefix: <UserIcon className="size-4 text-text-600" />,
//                     placeholder: 'Enter your email address',
//                     pattern: {
//                       value: EMAIL_REGEX,
//                       message: 'Invalid email address',
//                     },
//                   }}
//                 />
//                 <ButtonWizard
//                   onBtnClick={handleSubmit(handleSubmitForm)}
//                   addOnClass={
//                     'bg-primary-600 text-white py-2 text-xs rounded mb-8'
//                   }
//                   loading={isLoading}
//                 >
//                   Send Reset Password Link
//                 </ButtonWizard>
//               </form>
//             )}
//             <LinkWizard href={`/signin`} addOnClass="font-bold">
//               <>
//                 <ArrowLeftIcon className="size-3" />
//                 Back to log in
//               </>
//             </LinkWizard>
//           </div>
//         </div>
//       </div>
//       <div className="flex items-center justify-center border-l bg-slate-700">
//         <div className="h-96 w-96">
//           <img src={signInImage} alt="Sign In Image" loading="eager" />
//         </div>
//       </div>
//     </div>
//   );
// }
